<template>
  <div>
    <template v-if="feesV2Enabled">
    <div v-if="displayType === 'long'">
      <p>
        <strong>This illustration is hypothetical.</strong> It is shown for educational and
        illustrative purposes only and is not a prediction or projection of the performance of
        any investment, account, or strategy. No representation is made that any client will,
        or is likely to, achieve the projected fees or outcomes. Past performance is not
        indicative of future results.
      </p>

      <p>
        <strong>How fees are calculated.</strong> A portfolio's total fee rate is the weighted
        average expense ratio of its holdings plus the management fee and advisory fee entered
        for that portfolio. The weighted average expense ratio is calculated at the portfolio
        level using Eavg = Sum(Wi × Ei), where Wi is the weight of asset i and Ei is the
        expense ratio of asset i. Expense ratio data for each fund is from its most recent
        485BPOS SEC filing. Management fees and advisory fees (tiered and/or flat-dollar) are
        the values entered on the YourStake platform. Any annual flat-dollar fee is converted
        into an equivalent percentage of the shared account size so it can be combined into a
        single "Total Fees" rate; this rate reflects the first-year cost. Because flat-dollar
        fees do not grow with the account, the effective combined rate in later years will be
        lower than the year-one figure shown — the cumulative-fee chart below uses the
        per-year dollar amount directly, not this combined rate, so the chart remains accurate
        over the full period.
      </p>

      <p>
        <strong>Shared account size.</strong> To put all three portfolios on an equal-dollar
        footing, a single shared account size is used for the fee math. For this analysis,
        the account size used is {{ feeAumSourceName }}'s total dollars. Each portfolio's
        weighted-average expense ratio still comes from its own holdings, but advisory fees
        (including tier blending) and management fees are applied to this shared account
        size. Annual flat-dollar fees come from each portfolio's own fee schedule and are
        added per portfolio.
      </p>

      <p>
        <strong>Material assumptions.</strong> The projection assumes (1) the shared account
        size remains invested for the full 20-year period with no additional contributions,
        withdrawals, deposits, or rebalancing; (2) a constant 8% gross annual investment
        return, applied uniformly across all portfolios; (3) the fee inputs (expense ratios,
        advisory tiers, management fees, flat-dollar fees) remain constant for the full
        period; and (4) no taxes, trading costs, transaction costs, or other cash flows are
        reflected. Per-year fees are deducted from AUM before growth is applied, so the
        compounded growth rate is (1 + g)(1 − rate), not the gross rate alone. Cumulative
        fees for year n are the sum of fees from each prior year through year n.
      </p>

      <p>
        <strong>Risks and limitations.</strong> Actual results will differ — likely
        materially — from this illustration. Investment returns are not constant; they
        fluctuate, may be negative, and are not guaranteed. Fees, expense ratios, advisory
        rates, and management fees can change over time, and clients may add or withdraw
        funds, which would alter the outcome. The assumed 8% gross return is illustrative
        and does not represent expected or guaranteed performance of any specific portfolio.
      </p>

      <p>
        <strong>Comparing portfolios.</strong> The starting, proposed, and benchmark
        portfolios shown may differ in material respects beyond fees, including investment
        objectives, holdings, costs and expenses, liquidity, risk, fluctuation of principal
        and return, and tax treatment. A lower projected fee does not mean a portfolio is
        more suitable, will produce a higher net return, or is otherwise superior. The
        20-Year Cumulative Fee Difference reflects only the difference in projected fees
        over 20 years given the shared account size, assumed return, and fee inputs shown;
        it is not a projection of net investment performance or net return. When both a
        starting and proposed portfolio are selected, the difference is calculated between
        them; otherwise the comparison falls back to starting vs. benchmark, then to
        proposed vs. benchmark.
      </p>
    </div>

    <p v-else>
        <strong>Hypothetical illustration of fees only — not a prediction or projection of
          investment performance.</strong> All portfolios are compared using a single shared
        account size ({{ feeAumSourceName }}'s total dollars), assumed to stay invested for
        20 years at a constant 8% annual gross return with no contributions, withdrawals,
        taxes, or trading costs. Each portfolio's fees combine its holdings' expense ratios
        with its advisory, management, and flat-dollar fees; the "Total Fees" rate shown is
        the first-year combined rate, while the cumulative-fee chart uses per-year dollar
        amounts and stays accurate over the full period. Actual results will differ — likely
        materially — and a lower projected fee does not mean a higher net return. See the full
        methodology below for the complete assumptions, formulas, and limitations.
    </p>
    </template>

    <template v-else>
      <p v-if="displayType === 'long'">
        Fees presented in this analysis are based on the weighted average expense ratio of the portfolio, and may include advisory and/or management fees. The weighted average expense ratio is calculated at the portfolio level by multiplying the expense ratio of each holding by its percent weight in the portfolio, using the following formula:
        Eavg = Sum(Wi x Ei), where Eavg is the weighted average expense ratio of the portfolio, Wi is the weight of asset i, and Ei is the expense ratio of asset i. Expense ratio data for each fund as of its most recent 485BPOS SEC filing.

        Management and advisory fees may be manually added to the portfolio analysis through inputs on the YourStake website. A portfolio's total fees are equal to the weighted average expense ratio + management fees + advisory fees. Fees affect the performance of the portfolio.

        Total dollars in fees for each time period are calculated by multiplying the total portfolio value by the portfolio's total fees as a percent of total assets, assuming 8% investment returns, no contributions or withdrawals to the portfolio, no changes to the weighted average expense ratio of the portfolio, and no changes to the management and advisory fees associated with the portfolio. The chart displays cumulative fees paid at the end of each time period. Cumulative fees for time period n are calculated by summing the fees from each time period prior to and including period n.
        If a current portfolio is selected, fees for all portfolios are normalized to the current portfolio's total dollar value, otherwise fees are normalized to the proposed portfolio's value.
        20-yr difference is the absolute value of the difference in total fees paid over 20 years between the current and proposed portfolios if both are selected, otherwise the comparison is between the selected portfolio and the benchmark.
      </p>

      <p v-else>
        Assumes 8% annual investment returns, no contributions or withdrawals to the portfolio, and no changes to fund expense ratios, management fees, or advisory fees associated with the portfolio. If a current portfolio is selected, fees for all portfolios are normalized to the current portfolio's total dollar value, otherwise fees are normalized to the proposed portfolio's value. 20-yr difference is the absolute value of the difference in total fees paid over 20 years between the current and proposed portfolios if both are selected, otherwise the comparison is between the selected portfolio and the benchmark.
      </p>
    </template>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';

export default {
  name: 'FeesMethodology',
  props: ['displayType', 'portfolio', 'benchmark', 'optimizedPortfolio'],
  computed: {
    ...mapStores(useAdvisorStore),
    // The detailed tiered/flat-dollar methodology copy is part of the gated feature.
    // When off, fall back to the legacy methodology text.
    feesV2Enabled() {
      // Tiered-fee methodology copy is vue3-only; force off on vue2 (legacy copy).
      return false;
    },
    // Mirror the precedence in checkupFunctions.getExpenseRatio so the methodology
    // names the portfolio whose total_dollars was actually used as the shared AUM.
    feeAumSourceName() {
      if (this.portfolio?.total_dollars) return this.portfolio.name || 'the starting portfolio';
      if (this.optimizedPortfolio?.total_dollars) return this.optimizedPortfolio.name || 'the proposed portfolio';
      if (this.benchmark?.total_dollars) return this.benchmark.name || 'the benchmark';
      return 'the selected portfolio';
    },
  },
};
</script>


<style scoped>
/* The bold section lead-ins (e.g. "Shared account size.") should follow the
   surrounding text color so they stay legible in both contexts: dark text on the
   white in-page summary, and white text on the dark disclaimer footer. Without
   this they pick up the global `strong` color and blend into the dark footer. */
strong {
  color: inherit;
}
</style>
